import { useCallback, useEffect, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column, RowRenderProps, SortingRule } from 'react-table';

import { Action } from '../../../../../../typings/permissions';
import { useDialogContext } from '../../../../../contexts/DialogContext';
import {
  useHasPermissionForTeamProjects,
  useHasPermissionForTeamReleaseGroups,
} from '../../../../../helpers/permissions';
import {
  useAddProjectsToTeam,
  useAddReleaseGroupsToTeam,
  useRemoveProjectsFromTeam,
  useRemoveReleaseGroupsFromTeam,
} from '../../../../../hooks/api';
import { icons } from '../../../../generic';
import { DEFAULT_PAGE_SIZE } from '../../../../generic/Pagination/consts';
import SimpleReactTableWithPagination from '../../../../generic/SimpleReactTable/SimpleReactTableWithPagination';
import TableHeader from '../../../../generic/TableHeader';
import ModalAddTeamProjects from '../../../../modals/ModalAddTeamProjects';
import SettingsTableRowActions from '../../../shared/SettingsTableRowActions';
import type { PaginatedResponseWithTotal } from '../../../../../../shared/types/routes/pagination';
import type { GetTeamResponse } from '../../../../../../shared/types/routes/teams';

const defaultSortMode: SortingRule[] = [{ id: 'title', desc: false }];

type ContainerProps = {
  team: GetTeamResponse;
  search: string;
  isShowingAddProjectsModal: boolean;
  onHideAddProjectsModal: () => void;
  mode: 'projects' | 'releaseGroups';
};

type TeamDataRow =
  | {
      locator: string;
      title: string;
      type: 'project';
    }
  | {
      id: number;
      title: string;
      type: 'releaseGroup';
    };

type ContentProps = Omit<ContainerProps, 'mode'> & {
  rows: PaginatedResponseWithTotal<TeamDataRow>;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  isLoading: boolean;
  isFetching: boolean;
  sortMode: SortingRule;
  setSortMode: (sortMode: SortingRule) => void;
};

/**
 * Table of all of the projects and release groups for a team.
 */
const BaseTeamProjectsTable: VoidFunctionComponent<ContentProps> = ({
  team,
  search,
  isShowingAddProjectsModal,
  onHideAddProjectsModal,
  rows,
  page,
  setPage,
  pageSize,
  setPageSize,
  isLoading: isLoadingProjects,
  isFetching: isFetchingProjects,
  sortMode,
  setSortMode,
}) => {
  const { onConfirm } = useDialogContext();

  // Reset the page to 1 when the search term changes.
  useEffect(() => {
    setPage(1);
  }, [search, setPage]);

  //  Mutations
  const { mutateAsync: addProjectsToTeam, isLoading: isAddingProjects } = useAddProjectsToTeam();
  const { mutateAsync: addReleaseGroupsToTeam, isLoading: isAddingReleaseGroups } = useAddReleaseGroupsToTeam();
  const { mutateAsync: removeProjectsFromTeam, isLoading: isRemovingProjects } = useRemoveProjectsFromTeam();
  const { mutateAsync: removeReleaseGroupsFromTeam, isLoading: isRemovingReleaseGroups } =
    useRemoveReleaseGroupsFromTeam();

  //  Permissions
  const hasPermissionForTeamProjects = useHasPermissionForTeamProjects();
  const canRemoveProjects = hasPermissionForTeamProjects(Action.Remove, team.id);
  const hasPermissionForTeamReleaseGroups = useHasPermissionForTeamReleaseGroups();
  const canRemoveReleaseGroups = hasPermissionForTeamReleaseGroups(Action.Remove, team.id);

  //  Returns the click handler for removing projects/releaseGroups. Displays confirm modal first.
  const onRemoveClick = useCallback(
    ({ isProject, id, title }: { isProject: boolean; id: string | number; title: string }) =>
      () =>
        onConfirm({
          title: `Are you sure you want to remove "${title}"?`,
          body: `These ${isProject ? 'project(s)' : 'release group(s)'} will no longer be available to this team.`,
          onConfirm: () =>
            isProject
              ? removeProjectsFromTeam({ teamId: team.id, projectIds: [id as string] })
              : removeReleaseGroupsFromTeam({ teamId: team.id, releaseGroupIds: [id as number] }),
          confirmText: 'Remove',
          confirmBtnType: 'error',
        }),
    [onConfirm, removeProjectsFromTeam, removeReleaseGroupsFromTeam, team.id],
  );

  const columns = useMemo<Array<Column<TeamDataRow>>>(
    () => [
      {
        id: 'title',
        accessor: 'title',
        Header: <TableHeader title="Name" sort={{ id: 'title', currentMode: sortMode }} />,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
        Cell: ({ original }: { original: TeamDataRow } & RowRenderProps) => <span>{original.title}</span>,
        resizable: false,
      },
      {
        id: 'type',
        accessor: 'type',
        sortable: false,
        Header: <TableHeader title="Type" />,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
        Cell: ({ original }: { original: TeamDataRow }) => (
          <span>{original.type === 'project' ? 'Project' : 'Release Group'}</span>
        ),
        resizable: false,
        width: 250,
      },
      {
        id: 'actions',
        accessor: 'actions',
        Header: <TableHeader title="Actions" />,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
        Cell: ({ original }: { original: TeamDataRow }) =>
          original.type === 'releaseGroup' ? (
            <SettingsTableRowActions>
              <SettingsTableRowActions.IconButton
                tooltipId={`remove-release-group-btn-${original.id}`}
                tooltip={
                  !canRemoveReleaseGroups
                    ? 'You do not have permission to remove release groups from this team.'
                    : 'Remove Release Group'
                }
                icon={icons.trashRegular}
                isDisabled={!canRemoveReleaseGroups}
                onClick={onRemoveClick({
                  title: original.title,
                  id: original.id,
                  isProject: false,
                })}
              />
            </SettingsTableRowActions>
          ) : (
            <SettingsTableRowActions>
              <SettingsTableRowActions.IconButton
                tooltipId={`remove-project-btn-${original.locator}`}
                tooltip={
                  !canRemoveProjects
                    ? 'You do not have permission to remove projects from this team.'
                    : 'Remove Project'
                }
                icon={icons.trashRegular}
                isDisabled={!canRemoveProjects}
                onClick={onRemoveClick({
                  title: original.title,
                  id: original.locator,
                  isProject: true,
                })}
              />
            </SettingsTableRowActions>
          ),
        width: 70,
        resizable: false,
        sortable: false,
      },
    ],
    [sortMode, canRemoveReleaseGroups, canRemoveProjects, onRemoveClick],
  );

  return (
    <>
      <SimpleReactTableWithPagination
        data={rows.results}
        columns={columns}
        defaultSortMode={defaultSortMode}
        onSortedChanged={setSortMode}
        showPagination={(rows?.results?.length ?? 0) === pageSize}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        onPageSizeChange={setPageSize}
        onPageChange={setPage}
        totalPages={Math.ceil((rows?.totalCount ?? 0) / pageSize)}
        isLoading={
          isLoadingProjects ||
          isFetchingProjects ||
          isAddingProjects ||
          isAddingReleaseGroups ||
          isRemovingProjects ||
          isRemovingReleaseGroups
        }
      />
      {isShowingAddProjectsModal && (
        <ModalAddTeamProjects
          team={team}
          onDone={async (projectsIds: string[], releaseGroupIds: number[]) => {
            //  Note: these queries need to be done sequentially, as the release group can only be added after the projects are added.
            if (projectsIds.length > 0) {
              await addProjectsToTeam({ teamId: team.id, projectIds: projectsIds });
            }

            if (releaseGroupIds.length > 0) {
              await addReleaseGroupsToTeam({ teamId: team.id, releaseGroupIds });
            }

            onHideAddProjectsModal();
          }}
          onHide={onHideAddProjectsModal}
        />
      )}
    </>
  );
};

export default BaseTeamProjectsTable;
